import { useMemo, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { RevealLine, Reveal } from "../lib/motion";

// Rough equirectangular continent polygons on a 1000 x 480 viewBox
const CONTINENTS = [
  // North America
  [[150, 70], [250, 55], [320, 70], [340, 110], [300, 150], [275, 145], [255, 200], [205, 205], [180, 150], [150, 115]],
  // Central America
  [[248, 195], [285, 205], [312, 250], [292, 258], [262, 220]],
  // South America
  [[300, 255], [365, 250], [388, 305], [360, 385], [320, 410], [298, 360], [292, 300]],
  // Europe
  [[468, 88], [520, 78], [575, 82], [588, 120], [545, 150], [500, 148], [470, 120]],
  // Africa
  [[478, 165], [560, 158], [610, 175], [640, 245], [582, 350], [520, 360], [478, 275], [470, 210]],
  // Asia
  [[590, 70], [720, 58], [845, 70], [885, 120], [880, 165], [800, 200], [720, 215], [655, 185], [605, 150], [585, 105]],
  // India / SE Asia
  [[640, 190], [700, 195], [720, 240], [690, 255], [655, 225]],
  // Australia
  [[795, 320], [870, 315], [895, 360], [845, 400], [800, 375], [790, 345]],
];

function pointInPoly(x, y, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

const HUBS = [
  { name: "New York", x: 294, y: 132 },
  { name: "London", x: 500, y: 102 },
  { name: "Milano", x: 526, y: 118 },
  { name: "Dubai", x: 654, y: 172 },
  { name: "Singapore", x: 788, y: 236 },
  { name: "Tokyo", x: 887, y: 145 },
  { name: "São Paulo", x: 370, y: 300 },
];

const ARCS = [
  ["Milano", "New York"], ["Milano", "London"], ["Milano", "Dubai"],
  ["Dubai", "Singapore"], ["Singapore", "Tokyo"], ["New York", "São Paulo"], ["London", "New York"],
];

function arcPath(a, b) {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2 - Math.hypot(b.x - a.x, b.y - a.y) * 0.28;
  return `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
}

export const Global = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  const dots = useMemo(() => {
    const step = 13;
    const pts = [];
    for (let x = 130; x < 910; x += step) {
      for (let y = 45; y < 415; y += step) {
        const jx = x + (Math.sin(x * y) * 3);
        const jy = y + (Math.cos(x + y) * 3);
        if (CONTINENTS.some((p) => pointInPoly(jx, jy, p))) pts.push([jx, jy]);
      }
    }
    return pts;
  }, []);

  const hubByName = (n) => HUBS.find((h) => h.name === n);

  return (
    <section id="global" data-testid="global-section" className="py-24 sm:py-32 bg-[var(--paper-2)] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="max-w-2xl mb-14">
          <div className="overline mb-6">Presenza Globale</div>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-[var(--ink)]">
            <RevealLine>Un network di board</RevealLine>
            <RevealLine delay={0.08} className="italic text-[var(--petrol)]">e imprese, senza confini.</RevealLine>
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-6 text-lg text-[var(--ink-soft)]">Consulenza executive e progetti imprenditoriali che connettono i principali centri economici del mondo.</p>
          </Reveal>
        </div>

        <div ref={ref} className="relative w-full">
          <svg viewBox="0 0 1000 440" className="w-full h-auto" role="img" aria-label="Mappa del mondo con hub globali">
            {/* continent dots */}
            {dots.map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r="2.1" fill="var(--gold)"
                className={`map-dot ${inView ? "on" : ""}`}
                style={{ transitionDelay: `${(x / 1000) * 1.1}s` }} />
            ))}

            {/* connection arcs */}
            {ARCS.map(([a, b], i) => {
              const A = hubByName(a), B = hubByName(b);
              return (
                <motion.path key={i} d={arcPath(A, B)} fill="none" stroke="var(--accent)" strokeWidth="1"
                  strokeLinecap="round" opacity="0.55"
                  initial={{ pathLength: 0 }}
                  animate={inView ? { pathLength: 1 } : {}}
                  transition={{ duration: 1.4, ease: "easeInOut", delay: 1 + i * 0.25 }} />
              );
            })}

            {/* hub nodes */}
            {HUBS.map((h, i) => (
              <g key={h.name}>
                <circle cx={h.x} cy={h.y} r="3.4" fill="var(--gold)"
                  style={{ transformOrigin: `${h.x}px ${h.y}px`, animation: inView ? `hubPulse 3s ${1.2 + i * 0.3}s ease-out infinite` : "none" }} />
                <motion.circle cx={h.x} cy={h.y} r="3" fill="var(--petrol)"
                  initial={{ scale: 0 }} animate={inView ? { scale: 1 } : {}}
                  transition={{ delay: 1 + i * 0.15, type: "spring", stiffness: 260 }}
                  style={{ transformOrigin: `${h.x}px ${h.y}px` }} />
              </g>
            ))}
          </svg>

          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2">
            {HUBS.map((h, i) => (
              <Reveal key={h.name} delay={1 + i * 0.08} className="flex items-center gap-2 text-sm text-[var(--ink-soft)]">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)]" /> {h.name}
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
